import clsx from 'clsx';
import { useRef, useState } from 'react';

import { useDatePickerCalendar } from '../Calendar/CalendarContext';
import { DayView } from '../Calendar/Day/DayView';
import { MonthView } from '../Calendar/Month/MonthView';
import { YearView } from '../Calendar/Year/YearView';
import { useDatePickerConfig } from '../contexts/ConfigContext';
import { Presets } from '../Presets';
import { useDatePickerDropdown } from './DropdownContext';
import { DropdownNavigation } from './DropdownNavigation';

type CalendarView = 'day' | 'month' | 'year';
const DATE_PICKER_DEFAULT_HEIGHT = 316;

export function SgDatePickerMenu() {
  const { customPresets, presetsDisplayGrid, focusManagement } = useDatePickerConfig();
  const { isOpen, isPositioned, refs, floatingStyles, dropdownRef } = useDatePickerDropdown();
  const { calendarState, calendarProps } = useDatePickerCalendar();

  const [view, setView] = useState<CalendarView>('day');
  const gridRef = useRef<HTMLDivElement>(null);

  const handleMonthSelect = (month: number) => {
    calendarState.setFocusedDate(calendarState.focusedDate.set({ month }));
    setView('day');
  };

  const handleYearSelect = (year: number) => {
    calendarState.setFocusedDate(calendarState.focusedDate.set({ year }));
    setView('month');
  };

  // Handle Tab key on the last navigation button to focus into calendar
  const handleLastNavigationButtonKeyDown = (e: React.KeyboardEvent) => {
    focusManagement?.handleLastNavigationButtonTab(e, view);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={(node) => {
        refs.setFloating(node);
        if (dropdownRef && dropdownRef.current !== node) {
          dropdownRef.current = node;
        }
      }}
      role="dialog"
      aria-label="Date picker"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      className={clsx('sg-date-picker-dropdown-menu dropdown-menu bg-lvl1 p-0 z-10', { show: isOpen })}
      style={{
        ...floatingStyles,
        maxHeight: 'none',
        overflow: 'hidden',
        minHeight: view === 'day' ? DATE_PICKER_DEFAULT_HEIGHT : 'auto',
        visibility: isPositioned ? undefined : 'hidden',
        zIndex: 9999,
      }}
    >
      <div {...calendarProps} className="dropdown-container d-grid justify-content-between">
        <div className="flex-grow-1 d-flex flex-column align-items-top">
          <div style={{ width: '316px' }} className="d-flex flex-column">
            {/* Navigation Controls */}
            <DropdownNavigation
              view={view}
              onViewChange={setView}
              onLastNavigationButtonKeyDown={handleLastNavigationButtonKeyDown}
            />

            {/* Calendar Grid */}
            {view === 'day' && <DayView state={calendarState} gridRef={gridRef} />}

            {view === 'month' && <MonthView state={calendarState} onMonthSelect={handleMonthSelect} locale="en-GB" />}

            {view === 'year' && <YearView state={calendarState} onYearSelect={handleYearSelect} />}
          </div>
        </div>
        <Presets customPresets={customPresets} presetsDisplayGrid={presetsDisplayGrid} />
      </div>
    </div>
  );
}
